import { useCallback, useMemo, useState } from 'react';
import { DropdownProps, MenuProps } from 'antd';
import { ToolbarButtonProps } from './ToolbarButton';
import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import { renderMenuItem, ToolbarDropdownButton } from './ToolbarDropdownButton';
import { $patchStyleText } from '@lexical/selection';
import { useTranslation } from 'react-i18next';

export type FontType =
  | 'arial'
  | 'courier'
  | 'georgia'
  | 'newRoman'
  | 'trebuchet'
  | 'verdana';

export interface ToolbarFontDropdownButtonProps
  extends ToolbarButtonProps,
    Pick<DropdownProps, 'trigger' | 'overlay'> {
  selected?: FontType;
}

export const FONT_TYPE_META_BASE: Record<FontType, { css: string }> = {
  arial: { css: 'Arial, sans-serif' },
  courier: { css: '"Courier New", monospace' },
  georgia: { css: 'Georgia, serif' },
  newRoman: { css: '"Times New Roman", serif' },
  trebuchet: { css: '"Trebuchet MS", sans-serif' },
  verdana: { css: 'Verdana, sans-serif' },
};

export function applyFontFamily(font: string) {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    $patchStyleText(selection, { 'font-family': font });
  }
}

function useLocalizedFontMeta() {
  const { t } = useTranslation();

  return useMemo(() => {
    const entries = Object.entries(FONT_TYPE_META_BASE);

    return Object.fromEntries(
      entries.map(([key, val]) => [
        key,
        {
          css: val.css,
          label: t(`editor.font.${key}`),
        },
      ]),
    ) as Record<FontType, { label: string; css: string }>;
  }, [t]);
}

export function useFontFamilyMenu(editor: LexicalEditor) {
  const [selectedFamily, setSelectedFamily] = useState<FontType>('arial');
  const meta = useLocalizedFontMeta();

  const selectFont = useCallback(
    (family: FontType) => {
      setSelectedFamily(family);
      editor.update(() => {
        applyFontFamily(meta[family].css);
      });
    },
    [editor, meta],
  );

  const items = useMemo<MenuProps['items']>(() => {
    return (Object.keys(meta) as FontType[]).map((family) => ({
      key: family,
      label: renderMenuItem(null, meta[family].label),
      onClick: () => selectFont(family),
    }));
  }, [meta, selectFont]);

  return {
    fontFamilyMenu: items,
    selectedFontFamily: selectedFamily,
    meta,
  };
}

export function ToolbarFontDropdownButton(
  props: ToolbarFontDropdownButtonProps,
) {
  const { selected, ...dropDownProps } = props;

  const meta = useLocalizedFontMeta();
  const selectedMeta = selected ? meta[selected] : undefined;

  return (
    <ToolbarDropdownButton {...dropDownProps} selectedItem={selectedMeta} />
  );
}
